import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { cn } from "../../lib/utils";
import { usePageTitle } from "../../hooks/usePageTitle";
import { hasModule } from "../../utils/capabilitiesHelper";
import { showWhatsappExtraPages, showCreateConversation } from "../../utils/featureFlags";
import ConversationList from "../../components/whatsapp/chat/ConversationList";
import ChatHeader from "../../components/whatsapp/chat/ChatHeader";
import ConfirmDialog from "../../components/tailwind/ConfirmDialog";
import MessageList from "../../components/whatsapp/chat/MessageList";
import ChatComposer from "../../components/whatsapp/chat/ChatComposer";
import ChatEmptyState from "../../components/whatsapp/chat/ChatEmptyState";
import ChatTemplateMessageDialog from "../../components/whatsapp/chat/ChatTemplateMessageDialog";
import StartConversationDialog from "../../components/whatsapp/chat/StartConversationDialog";
import {
  CHAT_BACKGROUND_STYLE,
} from "../../components/whatsapp/chat/constants";
import {
  getFilteredConversations,
} from "../../components/whatsapp/chat/chatUtils";
import {
  selectCurrentConversation,
  selectConversationsLoading,
  selectConversationLoading,
  selectConversationError,
  selectConversationsList,
  setSearchQuery,
  selectConversation,
  fetchConversationsRequest,
  fetchConversationRequest,
  sendMessageRequest,
  selectSearchQuery,
  selectMessageError,
  createConversationRequest,
  selectCreateLoading,
  closeConversationRequest,
} from "../../store/conversationSlice";
import { MEDIA_TYPES } from "../../api/apiLists/sendMediaMessageApi";
import {
  sendMediaMessageRequest,
  selectMediaMessageError,
} from "../../store/mediaMessageSlice";
import { loadTemplatesRequest } from "../../store/whatsappSlice";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { ChatSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";

const detectMediaType = (file) => {
  const type = file.type;
  if (type.startsWith("image/")) return MEDIA_TYPES.IMAGE;
  if (type.startsWith("audio/")) return MEDIA_TYPES.AUDIO;
  if (type.startsWith("video/")) return MEDIA_TYPES.VIDEO;
  return MEDIA_TYPES.DOCUMENT;
};

export default function Chat() {
  usePageTitle("Chat");

  const location = useLocation();
  const dispatch = useDispatch();
  const searchQuery = useSelector(selectSearchQuery);
  const selectedChat = useSelector(selectCurrentConversation);
  const listLoading = useSelector(selectConversationsLoading);
  const conversationLoading = useSelector(selectConversationLoading);
  const messageError = useSelector(selectMessageError);
  const error = useSelector(selectConversationError);
  const conversations = useSelector(selectConversationsList);
  const mediaMessageError = useSelector(selectMediaMessageError);
  const rawTemplates = useSelector((state) => state.whatsapp?.templates || []);
  const templates = useMemo(() => {
    return rawTemplates.filter((t) => {
      const isApproved = String(t.status || "").toLowerCase() === "approved";
      const isUtility = String(t.category || "").toLowerCase() === "utility";
      return isApproved && isUtility;
    });
  }, [rawTemplates]);
  const currentAgentId = useSelector((state) => state.auth?.user?.userId);
  const hasWhatsApp = useSelector((state) =>
    hasModule(state.capabilities?.capabilities, 'WHATSAPP')
  );
  
  const canEdit = !selectedChat?.assignedAgentId || selectedChat?.assignedAgentId === currentAgentId;

  const phones = useSelector((state) => state.whatsapp?.phones || []);
  const createLoading = useSelector(selectCreateLoading);
  const closeLoading = useSelector((state) => state.conversation?.closeLoading);
  
  const [messageInput, setMessageInput] = useState("");
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isNewConvoDialogOpen, setIsNewConvoDialogOpen] = useState(false);
  const [isTemplateOnly, setIsTemplateOnly] = useState(false);
  const [templateOnlyReason, setTemplateOnlyReason] = useState(null);
  const [activeTab, setActiveTab] = useState("ACTIVE");
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  const pendingPhoneRef = useRef(location.state?.phone || null);

  const displayError = messageError || mediaMessageError;

  useEffect(() => {
    if (!selectedChat) {
      setIsTemplateOnly(false);
      setTemplateOnlyReason(null);
      return;
    }

    const hasNoMessages = !selectedChat.messages || selectedChat.messages.length === 0;

    if (hasNoMessages || !selectedChat.lastMessageAt) {
      setIsTemplateOnly(true);
      setTemplateOnlyReason('NEW_CONVERSATION');
      return;
    }

    const getNormalizedTimestamp = () => {
      const lastMessageAt = Number(selectedChat.lastMessageAt);
      if (!lastMessageAt) return 0;
      return lastMessageAt > 9999999999 ? lastMessageAt : lastMessageAt * 1000;
    };

    const check24Hours = () => {
      const diff = Date.now() - getNormalizedTimestamp();
      const expired = diff > 24 * 60 * 60 * 1000;
      setIsTemplateOnly(expired);
      setTemplateOnlyReason(expired ? 'PAST_24_HOURS' : null);
    };

    check24Hours();
    const interval = setInterval(check24Hours, 60 * 1000);

    return () => clearInterval(interval);
  }, [selectedChat, selectedChat?.lastMessageAt, selectedChat?.messages?.length]);

  useEffect(() => {
    if (!hasWhatsApp) return;
    dispatch(fetchConversationsRequest({ status: "OPEN", limit: 10 }));
  }, [dispatch, hasWhatsApp]);

  useEffect(() => {
    if (!hasWhatsApp) return;
    dispatch(loadTemplatesRequest({ loadAll: true }));
    dispatch({ type: 'FETCH_WABA_IDS' });
  }, [dispatch, hasWhatsApp]);

  const conversationsList = useMemo(
    () => (conversations?.allIds || [])
      .map((id) => conversations?.byId?.[id])
      .filter((chat) => chat && chat.status !== 'CLOSED'),
    [conversations]
  );

  const { tabCounts, groupedConversations } = useMemo(() => {
    const counts = { ACTIVE: 0, REQUESTING: 0 };
    const groups = { ACTIVE: [], REQUESTING: [] };
    
    const sortByRecent = (a, b) =>
      Number(b.lastMessageAt || 0) - Number(a.lastMessageAt || 0);

    conversationsList.forEach(chat => {
      const mode = (chat.mode || '').toUpperCase();
      let category = 'ACTIVE';
      
      if (mode === 'WAITING_AGENT') {
        category = 'REQUESTING';
      }
      
      counts[category]++;
      groups[category].push(chat);
    });

    Object.keys(groups).forEach((category) => {
      groups[category].sort(sortByRecent);
    });
    
    return { tabCounts: counts, groupedConversations: groups };
  }, [conversationsList]);

  const filteredConversations = useMemo(
    () => getFilteredConversations(groupedConversations[activeTab] || [], searchQuery),
    [groupedConversations, activeTab, searchQuery]
  );

  const selectedConversationId = useSelector((state) => state.conversation?.selectedConversationId);

  useEffect(() => {
    if (selectedConversationId) {
      setSelectedChatId(selectedConversationId);
      setIsNewConvoDialogOpen(false);
    }
  }, [selectedConversationId]);

  useEffect(() => {
    setIsResolveDialogOpen(false);
  }, [selectedChatId]);

  const handleConfirmResolve = () => {
    if (!selectedChat?.id) return;
    dispatch(closeConversationRequest({ conversationId: selectedChat.id }));
  };

  useEffect(() => {
    const phone = pendingPhoneRef.current;
    if (!phone || !conversationsList.length) return;

    const normalizedPhone = phone.replace(/\D/g, "");
    const match = conversationsList.find((chat) => {
      const chatPhone = (chat.phone || "").replace(/\D/g, "");
      return chatPhone && (chatPhone.includes(normalizedPhone) || normalizedPhone.includes(chatPhone));
    });

    if (match) {
      setSelectedChatId(match.id);
      dispatch(selectConversation(match.id));
      dispatch(fetchConversationRequest({ conversationId: match.id }));
      pendingPhoneRef.current = null;
      return;
    }

    dispatch(setSearchQuery(phone));
    pendingPhoneRef.current = null;
  }, [conversationsList, dispatch]);

  const handleSelectChat = (chatId) => {
    setSelectedChatId(chatId);
    dispatch(selectConversation(chatId));
    dispatch(fetchConversationRequest({ conversationId: chatId }));
  };

  const handleSendTextMessage = () => {
    const trimmedMessage = messageInput.trim();
    const targetChatId = selectedChatId || selectedChat?.id;
    if (!trimmedMessage || !targetChatId) return;

    dispatch(
      sendMessageRequest({
        conversationId: targetChatId,
        contentType: 'TEXT',
        contentData: {
          text: trimmedMessage,
        },
      })
    );
    setMessageInput('');
  };

  const handleMediaSelect = (file) => {
    setSelectedMedia({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
  };

  const handleSendMediaMessage = () => {
    const targetChatId = selectedChatId || selectedChat?.id;
    if (!selectedMedia || !targetChatId) return;

    const mediaType = detectMediaType(selectedMedia.file);

    dispatch(sendMediaMessageRequest({
      file: selectedMedia.file,
      conversationId: targetChatId,
      mediaType,
      caption: messageInput.trim() || undefined,
      filename: mediaType === MEDIA_TYPES.DOCUMENT ? selectedMedia.file.name : undefined,
    }));

    setMessageInput("");
    setSelectedMedia(null);
  };

  const handleSendMessage = () => {
    if (selectedMedia) {
      handleSendMediaMessage();
    } else {
      handleSendTextMessage();
    }
  };

  const handleSendTemplateMessage = (templatePayload) => {
    const targetChatId = selectedChatId || selectedChat?.id;
    if (!targetChatId || !templatePayload) return;

    dispatch(
      sendMessageRequest({
        conversationId: targetChatId,
        contentType: "TEMPLATE",
        contentData: {
          template: templatePayload,
        },
      })
    );
  };

  const isMainPanelLoading =
    (conversationLoading && !selectedChat) || (!selectedChatId && listLoading);

  const isPageLoading = listLoading && filteredConversations.length === 0 && !selectedChatId;

  if (isPageLoading) {
    return <ChatSkeleton />;
  }

  return (
    <div className={waShell(whatsappPageShell.fill, "flex-1 min-h-0 -m-3 overflow-hidden animate-fade-in")}>
      <div className="h-full min-h-0 flex w-full border border-gray-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
        <ConversationList
          conversations={filteredConversations}
          searchQuery={searchQuery}
          onSearchQueryChange={(query) => dispatch(setSearchQuery(query))}
          selectedChatId={selectedChatId}
          onSelectChat={handleSelectChat}
          isHiddenOnMobile={Boolean(selectedChatId)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabCounts={tabCounts}
          onNewConversationClick={() => setIsNewConvoDialogOpen(true)}
        />

        <div
          className={cn(
            "flex-1 min-w-0 flex flex-col bg-[#efeae2] dark:bg-slate-950",
            !selectedChatId ? "hidden md:flex" : "flex"
          )}
        >
          {error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-500 dark:text-red-400 font-semibold mb-2">Error loading conversation</p>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
              </div>
            </div>
          ) : isMainPanelLoading ? (
            <div className="flex-1 flex flex-col p-6 space-y-4 animate-pulse" aria-busy="true" aria-label="Loading conversation">
              <div className="h-12 w-2/3 max-w-sm rounded-2xl bg-gray-200/80 dark:bg-slate-700" />
              <div className="h-16 w-1/2 max-w-xs ml-auto rounded-2xl bg-gray-200/80 dark:bg-slate-700" />
              <div className="h-10 w-1/3 max-w-[200px] rounded-2xl bg-gray-200/80 dark:bg-slate-700" />
            </div>
          ) : selectedChat ? (
            <>
              <ChatHeader
                chat={selectedChat}
                onBack={() => setSelectedChatId(null)}
                onResolve={() => setIsResolveDialogOpen(true)}
              />
              <MessageList
                messages={selectedChat.messages}
                backgroundStyle={CHAT_BACKGROUND_STYLE}
              />
              
              {displayError && (
                <div className="px-4 py-2 bg-red-50 dark:bg-red-950 border-t border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-300">{displayError}</p>
                </div>
              )}
              
              {!canEdit && (
                <div className="bg-amber-50 dark:bg-amber-900/30 border-t border-amber-200 dark:border-amber-800 p-2 text-center text-sm text-amber-800 dark:text-amber-200 font-medium">
                  View Only: This conversation is assigned to another agent.
                </div>
              )}

              {canEdit && isTemplateOnly && (
                <div className="bg-red-50 dark:bg-red-900/30 border-t border-red-200 dark:border-red-800 p-2 text-center text-sm text-red-800 dark:text-red-200 font-medium">
                  {templateOnlyReason === 'NEW_CONVERSATION'
                    ? 'This is a new conversation. You can only send template messages to initiate contact.'
                    : 'It has been more than 24 hours since user replied, now you can send only templated messages by clicking template button.'}
                </div>
              )}

              <ChatComposer
                messageInput={messageInput}
                onMessageInputChange={setMessageInput}
                onSend={handleSendMessage}
                onTemplateClick={() => setIsTemplateDialogOpen(true)}
                selectedMedia={selectedMedia}
                onMediaSelect={handleMediaSelect}
                onMediaRemove={handleRemoveMedia}
                isLoading={false}
                canEdit={canEdit}
                isPast24Hours={isTemplateOnly}
                templateOnlyReason={templateOnlyReason}
              />
              <ChatTemplateMessageDialog
                open={isTemplateDialogOpen}
                onOpenChange={setIsTemplateDialogOpen}
                templates={templates}
                onSubmit={handleSendTemplateMessage}
                isSending={false}
              />
              <ConfirmDialog
                open={isResolveDialogOpen}
                onOpenChange={setIsResolveDialogOpen}
                onCancel={() => setIsResolveDialogOpen(false)}
                onConfirm={handleConfirmResolve}
                title="Resolve Conversation"
                description="Are you sure you want to resolve this conversation? This will mark the conversation as closed and remove it from your active list."
                confirmLabel="Resolve"
                cancelLabel="Cancel"
                tone="warning"
                confirmVariant="primary"
                loading={closeLoading}
              />
            </>
          ) : (
            <ChatEmptyState />
          )}

          {showCreateConversation && (
            <StartConversationDialog
              open={isNewConvoDialogOpen}
              onOpenChange={setIsNewConvoDialogOpen}
              phones={phones}
              onSubmit={(payload) => {
                dispatch(createConversationRequest(payload));
              }}
              isLoading={createLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
}
